import { Navigate, type RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AdminRoute } from '@/components/AdminRoute';
import { GuestRoute } from '@/components/GuestRoute';
import { HomePage } from '@/pages/HomePage';
import { LoginPage } from '@/pages/LoginPage';
import { RegisterPage } from '@/pages/RegisterPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { VerifyEmailPage } from '@/pages/VerifyEmailPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { AssetsPage } from '@/pages/AssetsPage';
import { DebtsPage } from '@/pages/DebtsPage';
import { CreditsPage } from '@/pages/CreditsPage';
import { TransactionHistoryPage } from '@/pages/credits/TransactionHistoryPage';
import { DataPage } from '@/pages/DataPage';
import { PurgeDataPage } from '@/pages/PurgeDataPage';
import { FeedbackPage } from '@/pages/FeedbackPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { AttributionPage } from '@/pages/settings/AttributionPage';
import { PreferencesPage } from '@/pages/settings/PreferencesPage';
import { NotificationsPage } from '@/pages/settings/NotificationsPage';
import { ConnectedAppsPage } from '@/pages/settings/ConnectedAppsPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { EmailChangeConfirmPage } from '@/pages/profile/EmailChangeConfirmPage';
import { EmailChangeVerifyPage } from '@/pages/profile/EmailChangeVerifyPage';
import { AccountDeleteConfirmPage } from '@/pages/AccountDeleteConfirmPage';
import { AdminPage } from '@/pages/admin/AdminPage';
import { AdminUsersPage } from '@/pages/admin/AdminUsersPage';
import { AdminTransactionsPage } from '@/pages/admin/AdminTransactionsPage';
import { AdminPurchasesPage } from '@/pages/admin/AdminPurchasesPage';
import { AdminFeedbackPage } from '@/pages/admin/AdminFeedbackPage';
import { CircuitBreakerTestPage } from '@/pages/dev/CircuitBreakerTestPage';
import { ExchangeRatesPage } from '@/pages/dev/ExchangeRatesPage';
import { LedgerDetailPage } from '@/pages/LedgerDetail/LedgerDetailPage';
import { ValuesTab } from '@/pages/LedgerDetail/ValuesTab/ValuesTab';
import { CashFlowTab } from '@/pages/LedgerDetail/CashFlowTab/CashFlowTab';
import { AnalyticsTab } from '@/pages/LedgerDetail/AnalyticsTab/AnalyticsTab';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { OAuthConsentPage } from '@/pages/OAuthConsentPage';

/**
 * The application route tree, as data rather than JSX.
 *
 * App.tsx renders it with `useRoutes`. Keeping it as data lets
 * routes/__tests__/manifest.test.ts flatten the real tree and compare it to
 * routes/manifest.ts, so the two can never drift apart.
 *
 * Child paths are relative, as React Router requires. The test joins them.
 *
 * Every path here must have a matching entry in ROUTES, and the catch-all is
 * the only exception. Add a route here and the test tells you to declare it.
 */
export const appRoutes: RouteObject[] = [
    { path: '/', element: <HomePage /> },
    {
        path: '/login',
        element: (
            <GuestRoute>
                <LoginPage />
            </GuestRoute>
        ),
    },
    {
        path: '/register',
        element: (
            <GuestRoute>
                <RegisterPage />
            </GuestRoute>
        ),
    },
    {
        path: '/forgot-password',
        element: (
            <GuestRoute>
                <ForgotPasswordPage />
            </GuestRoute>
        ),
    },
    { path: '/reset-password', element: <ResetPasswordPage /> },
    // The consent page guards itself so it can keep its own query string in returnTo.
    { path: '/oauth/consent', element: <OAuthConsentPage /> },
    { path: '/verify-email', element: <VerifyEmailPage /> },
    {
        path: '/onboarding',
        element: (
            <ProtectedRoute>
                <OnboardingPage />
            </ProtectedRoute>
        ),
    },
    {
        path: '/dashboard',
        element: (
            <ProtectedRoute>
                <DashboardPage />
            </ProtectedRoute>
        ),
    },
    {
        path: '/assets',
        element: (
            <ProtectedRoute>
                <AssetsPage />
            </ProtectedRoute>
        ),
    },
    {
        path: '/debts',
        element: (
            <ProtectedRoute>
                <DebtsPage />
            </ProtectedRoute>
        ),
    },
    // Ledger detail page with nested tab routes.
    {
        path: '/ledgers/:id',
        element: (
            <ProtectedRoute>
                <LedgerDetailPage />
            </ProtectedRoute>
        ),
        children: [
            { index: true, element: <Navigate to="values" replace /> },
            { path: 'values', element: <ValuesTab /> },
            { path: 'cash-flow', element: <CashFlowTab /> },
            { path: 'analytics', element: <AnalyticsTab /> },
        ],
    },
    {
        path: '/credits',
        element: (
            <ProtectedRoute>
                <CreditsPage />
            </ProtectedRoute>
        ),
    },
    {
        path: '/credits/history',
        element: (
            <ProtectedRoute>
                <TransactionHistoryPage />
            </ProtectedRoute>
        ),
    },
    {
        path: '/data',
        element: (
            <ProtectedRoute>
                <DataPage />
            </ProtectedRoute>
        ),
    },
    {
        path: '/data/purge',
        element: (
            <ProtectedRoute>
                <PurgeDataPage />
            </ProtectedRoute>
        ),
    },
    // Redirect the old /export route to /data.
    { path: '/export', element: <Navigate to="/data" replace /> },
    {
        path: '/feedback',
        element: (
            <ProtectedRoute>
                <FeedbackPage />
            </ProtectedRoute>
        ),
    },
    {
        path: '/profile',
        element: (
            <ProtectedRoute>
                <ProfilePage />
            </ProtectedRoute>
        ),
    },
    { path: '/email-change/confirm', element: <EmailChangeConfirmPage /> },
    { path: '/email-change/verify', element: <EmailChangeVerifyPage /> },
    { path: '/account/delete/confirm', element: <AccountDeleteConfirmPage /> },
    {
        path: '/settings',
        element: (
            <ProtectedRoute>
                <SettingsPage />
            </ProtectedRoute>
        ),
        children: [
            // The old Tags and Collections pages were absorbed into Attribution.
            { path: 'tags', element: <Navigate to="/settings/attribution" replace /> },
            { path: 'collections', element: <Navigate to="/settings/attribution" replace /> },
            { path: 'attribution', element: <AttributionPage /> },
            { path: 'preferences', element: <PreferencesPage /> },
            { path: 'notifications', element: <NotificationsPage /> },
            { path: 'connected-apps', element: <ConnectedAppsPage /> },
        ],
    },
    {
        path: '/admin',
        element: (
            <AdminRoute>
                <AdminPage />
            </AdminRoute>
        ),
        children: [
            { path: 'users', element: <AdminUsersPage /> },
            { path: 'transactions', element: <AdminTransactionsPage /> },
            { path: 'purchases', element: <AdminPurchasesPage /> },
            { path: 'feedback', element: <AdminFeedbackPage /> },
            { path: 'dev/circuit-breaker', element: <CircuitBreakerTestPage /> },
            { path: 'dev/exchange-rates', element: <ExchangeRatesPage /> },
        ],
    },
    { path: '*', element: <NotFoundPage /> },
];
